import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { useCart } from "../../contexts/cartContext";
import { showErrorToast } from "../../../../utils/toast";
import { Check, Delete, ShoppingCart, X } from "lucide-react";

const isCompactPosDevice = () => {
    if (typeof window === "undefined" || typeof navigator === "undefined") {
        return false;
    }

    const width = window.innerWidth || document.documentElement?.clientWidth || 0;
    const maxTouchPoints = Number(navigator.maxTouchPoints || navigator.msMaxTouchPoints || 0);
    const userAgent = navigator.userAgent || "";
    const hasTouch = maxTouchPoints > 0 || "ontouchstart" in window;
    const coarsePointer = window.matchMedia?.("(pointer: coarse)")?.matches;
    const noHover = window.matchMedia?.("(hover: none)")?.matches;
    const tabletOrMobileUa = /Android|iPhone|iPad|iPod|Mobile|Tablet|HarmonyOS|HUAWEI/i.test(userAgent);
    const desktopOsUa = /Windows NT|Macintosh|Mac OS X|CrOS|Linux x86_64/i.test(userAgent) && !tabletOrMobileUa;

    if (width <= 1023) {
        return true;
    }

    if (desktopOsUa) {
        return false;
    }

    return Boolean(hasTouch && (coarsePointer || noHover || tabletOrMobileUa || width <= 1366));
};

const EditCartItemModal = ({ item, onClose }) => {
    const { updateCartItem } = useCart();
    const [qty, setQty] = useState(String(item.qty));
    const [weight, setWeight] = useState(item.weight || "");
    const [error, setError] = useState("");
    const [isKeypadLayout, setIsKeypadLayout] = useState(isCompactPosDevice);

    const modalRef = useRef(null);
    const qtyInputRef = useRef(null);

    // Take focus away from the barcode scanner field when the modal opens.
    useLayoutEffect(() => {
        const focusQtyInput = () => {
            if (!qtyInputRef.current) return;
            if (document.activeElement && document.activeElement !== qtyInputRef.current) {
                document.activeElement.blur?.();
            }
            qtyInputRef.current?.focus({ preventScroll: true });
            qtyInputRef.current?.select();
        };

        const frameId = window.requestAnimationFrame(focusQtyInput);
        const timeoutId = window.setTimeout(focusQtyInput, 80);
        const fallbackTimeoutId = window.setTimeout(focusQtyInput, 180);

        return () => {
            window.cancelAnimationFrame(frameId);
            window.clearTimeout(timeoutId);
            window.clearTimeout(fallbackTimeoutId);
        };
    }, [isKeypadLayout, item.id, item.lineId]);

    useEffect(() => {
        const handleResize = () => setIsKeypadLayout(isCompactPosDevice());
        window.addEventListener("resize", handleResize);
        window.addEventListener("orientationchange", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
            window.removeEventListener("orientationchange", handleResize);
        };
    }, []);

    // Prevent background scroll while modal is open
    useEffect(() => {
        document.body.style.overflow = "hidden";
        return () => (document.body.style.overflow = "auto");
    }, []);

    // Close modal when clicking outside
    const handleOutsideClick = (e) => {
        if (modalRef.current && !modalRef.current.contains(e.target)) {
            onClose();
        }
    };

    // Handle Enter key for saving
    const handleKeyDown = (e) => {
        e.stopPropagation();
        if (e.key === "Enter") handleSave();
        if (e.key === "Escape") onClose();
    };

    const handleSave = () => {
        const parsedQty = Number(qty);
        if (!qty || Number.isNaN(parsedQty) || parsedQty <= 0) {
            setError("Quantity must be at least 1");
            return;
        }
        if (item.stock !== undefined && item.stock !== null && parsedQty > item.stock) {
            showErrorToast(`Only ${item.stock} left in stock`);
            return;
        }
        if (item.soldByWeight && weight < 0) {
            setError("Weight cannot be negative");
            return;
        }

        updateCartItem(item.lineId || item.id, { qty: parsedQty, weight });
        onClose();
    };

    const appendDigit = (digit) => {
        setError("");
        setQty((prev) => {
            const next = `${prev}${digit}`.replace(/^0+(?=\d)/, "");
            return next.slice(0, 4);
        });
    };

    if (isKeypadLayout) {
        const parsedQty = Number(qty);

        return (
            <div
                data-pos-modal="true"
                className="fixed inset-0 z-[70] flex items-center justify-center bg-black/45 px-4 py-6 backdrop-blur-sm"
                onClick={handleOutsideClick}
                onKeyDown={handleKeyDown}
                tabIndex={-1}
            >
                <div
                    ref={modalRef}
                    className="w-full max-w-md overflow-hidden rounded-[28px] bg-white shadow-[0_28px_90px_rgba(15,23,42,0.32)]"
                >
                    <div className="border-b border-slate-200 px-5 py-4">
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex min-w-0 items-start gap-3">
                                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-white shadow-sm" style={{ background: "var(--color-primary)" }}>
                                    <ShoppingCart className="h-6 w-6" />
                                </div>
                                <div className="min-w-0">
                                    <h2 className="text-xl font-semibold text-slate-950">Edit Quantity</h2>
                                    <p className="mt-1 truncate text-sm font-medium text-slate-700">{item.name}</p>
                                    {item.stock !== undefined && item.stock !== null && (
                                        <p className="text-xs text-slate-500">{Number(item.stock || 0)} left in stock</p>
                                    )}
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:bg-slate-50 hover:text-slate-900"
                                aria-label="Close quantity editor"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                    </div>

                    <div className="space-y-4 p-5">
                        <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
                            <div className="mb-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-right text-4xl font-semibold tracking-tight text-slate-950 shadow-inner">
                                {qty || "0"}
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                                    <button
                                        key={digit}
                                        type="button"
                                        onClick={() => appendDigit(String(digit))}
                                        className="h-14 rounded-2xl border border-slate-200 bg-white text-xl font-semibold text-slate-900 shadow-sm transition hover:-translate-y-0.5 hover:border-slate-300 hover:bg-slate-50"
                                    >
                                        {digit}
                                    </button>
                                ))}
                                <button
                                    type="button"
                                    onClick={() => {
                                        setError("");
                                        setQty("");
                                    }}
                                    className="h-14 rounded-2xl border border-slate-200 bg-white text-sm font-semibold text-slate-500 shadow-sm transition hover:bg-slate-50"
                                >
                                    Clear
                                </button>
                                <button
                                    type="button"
                                    onClick={() => appendDigit("0")}
                                    className="h-14 rounded-2xl border border-slate-200 bg-white text-xl font-semibold text-slate-900 shadow-sm transition hover:-translate-y-0.5 hover:border-slate-300 hover:bg-slate-50"
                                >
                                    0
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setError("");
                                        setQty((prev) => prev.slice(0, -1));
                                    }}
                                    className="flex h-14 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:bg-slate-50"
                                    aria-label="Delete last digit"
                                >
                                    <Delete className="h-5 w-5" />
                                </button>
                            </div>
                        </div>

                        {error && <p className="rounded-2xl bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-600">{error}</p>}

                        <div className="grid grid-cols-2 gap-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="h-12 rounded-2xl border border-slate-200 bg-white text-sm font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSave}
                                disabled={!parsedQty}
                                className="flex h-12 items-center justify-center gap-2 rounded-2xl text-sm font-semibold text-white shadow-sm transition disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
                                style={parsedQty ? { background: "var(--color-primary)" } : undefined}
                                onMouseOver={parsedQty ? (event) => {
                                    event.currentTarget.style.background = "color-mix(in srgb, var(--color-primary) 82%, black)";
                                } : undefined}
                                onMouseOut={parsedQty ? (event) => {
                                    event.currentTarget.style.background = "var(--color-primary)";
                                } : undefined}
                            >
                                <Check className="h-4 w-4" />
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            data-pos-modal="true"
            className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/45 px-4 py-6 backdrop-blur-sm"
            onClick={handleOutsideClick}
            onKeyDown={handleKeyDown}
            tabIndex={-1}
        >
            <div
                ref={modalRef}
                className="w-full max-w-md overflow-hidden rounded-[28px] bg-white shadow-[0_28px_90px_rgba(15,23,42,0.32)]"
            >
                <div className="border-b border-slate-200 px-5 py-4">
                    <div className="flex items-start justify-between gap-4">
                        <div className="flex min-w-0 items-start gap-3">
                            <div
                                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-white shadow-sm"
                                style={{ background: "var(--color-primary)" }}
                            >
                                <ShoppingCart className="h-6 w-6" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">Cart Item</p>
                                <h2 className="mt-1 truncate text-xl font-semibold text-slate-950">Edit Quantity</h2>
                                <p className="mt-1 truncate text-sm font-medium text-slate-600">{item.name}</p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:bg-slate-50 hover:text-slate-900"
                            aria-label="Close quantity editor"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                <div className="space-y-5 p-5">
                    {item.stock !== undefined && item.stock !== null && (
                        <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                            <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Available Stock</span>
                            <span className="rounded-full bg-white px-3 py-1 text-sm font-bold text-slate-800 shadow-sm">
                                {Number(item.stock || 0)} left
                            </span>
                        </div>
                    )}

                    <div>
                        <label className="mb-2 block text-xs font-bold uppercase tracking-[0.18em] text-slate-400">Quantity</label>
                        <input
                            ref={qtyInputRef}
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={qty}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val === "" || /^[0-9]+$/.test(val)) {
                                    setError("");
                                    setQty(val);
                                }
                            }}
                            onKeyDown={(e) => {
                                if (["e", "E", "+", "-", "."].includes(e.key)) {
                                    e.preventDefault();
                                }
                            }}
                            className="h-14 w-full rounded-2xl border border-slate-200 bg-white px-4 text-lg font-semibold text-slate-950 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-[var(--color-primary)] focus:ring-4 focus:ring-slate-100"
                            min={1}
                        />
                    </div>

                    {item.soldByWeight && (
                        <div>
                            <label className="mb-2 block text-xs font-bold uppercase tracking-[0.18em] text-slate-400">Weight (g)</label>
                        <input
                            type="number"
                            value={weight}
                            onChange={(e) => setWeight(e.target.value)}
                            className="h-14 w-full rounded-2xl border border-slate-200 bg-white px-4 text-lg font-semibold text-slate-950 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-[var(--color-primary)] focus:ring-4 focus:ring-slate-100"
                            min={0}
                        />
                        </div>
                    )}

                    {error && <p className="rounded-2xl bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-600">{error}</p>}

                    <div className="grid grid-cols-2 gap-3 pt-1">
                        <button
                            type="button"
                            onClick={onClose}
                            className="h-12 rounded-2xl border border-slate-200 bg-slate-50 text-sm font-semibold text-slate-600 shadow-sm transition hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus:ring-4 focus:ring-slate-100"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSave}
                            className="flex h-12 items-center justify-center gap-2 rounded-2xl text-sm font-semibold text-white shadow-sm transition focus:outline-none focus:ring-4 focus:ring-slate-100"
                            style={{ background: 'var(--color-primary)' }}
                            onMouseOver={e => e.currentTarget.style.background = 'color-mix(in srgb, var(--color-primary) 82%, black)'}
                            onMouseOut={e => e.currentTarget.style.background = 'var(--color-primary)'}
                        >
                            <Check className="h-4 w-4" />
                            Save
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditCartItemModal;
